/** @jsxImportSource @emotion/react */
import { useState, useContext, useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { Grid, InputAdornment } from '@mui/material'
import {
  Section,
  Link,
  Alert,
  FormikSelect,
  FormikTextField,
  FormikCheckbox,
} from '../../../../components'
import config from '../../../../../lib/config/de-at.config.json'
import { locales } from '../../../../../lib/common/enums/locales'
import { FormContext } from '../../../../context/form-context'
import { CacheContext } from '../../../../context/cache-context'
import { api as productionApi } from '../../../../../lib/utils/api'
import { mockApi } from '../../../../../lib/utils/mock-api'
import { content } from './snowload.content'
import SnowZoneModal from './snowzone-modal'
import SnowEquipmentModal from './snow-equipment-modal'
import SnowProtectionModal from './snow-protection-modal'

let api = process.env.REACT_APP_USE_MOCKS === 'true' ? mockApi : productionApi

const protectionProps = config.protection.properties
const allEquipmentItems = protectionProps.snowCatcherEquipmentKind.items
const MAX_PITCH = 35
const MAX_SNOW_CATCHER_ROWS = 2

export default function SnowLoad(props) {
  const { formState } = useContext(FormContext)
  const { cache } = useContext(CacheContext)
  const formik = useFormikContext()

  const equipmentOptions = useMemo(() => {
    const bearingResistance = formState?.tile?.bearingResistance
    if (!bearingResistance) return allEquipmentItems
    return allEquipmentItems.filter((item) => item.id in bearingResistance)
  }, [formState?.tile?.bearingResistance])
  const [isSnowZoneModalOpen, setSnowZoneModalOpen] = useState(false)
  const [isSnowEquipmentModalOpen, setSnowEquipmentModalOpen] = useState(false)
  const [isSnowProtectionModalOpen, setSnowProtectionModalOpen] = useState(false)
  const [isSnowNoseMandatory, setSnowNoseMandatory] = useState(false)
  const [hasExcessiveRows, setHasExcessiveRows] = useState(false)

  const { currentStep } = props
  const snowCatcherEquipmentKind = formik.values?.snowCatcherEquipmentKind
  const snowProtectionKind = formik.values?.snowProtectionKind
  const snowGroundLoad = formik.values?.snowGroundLoad

  /**
   * Pre-calculate snow guard rows when entering step 4 or when snow fields change.
   * Auto-enables snow noses when rows > 2.
   */
  useEffect(() => {
    if (currentStep !== 3) return

    const locale = formState?.place?.locale
    const roofPitch = formState?.dimensions?.roofPitch
    const hipPitch = formState?.dimensions?.hipPitch

    const hasAllInputs =
      Boolean(snowCatcherEquipmentKind) &&
      Boolean(snowProtectionKind) &&
      Boolean(snowGroundLoad) &&
      Boolean(formState?.dimensions) &&
      Boolean(formState?.tile) &&
      Boolean(formState?.place)

    if (!hasAllInputs) {
      setHasExcessiveRows(false)
      return
    }

    const isSnowNoseForPreCalc =
      locale === locales.austria && (roofPitch >= MAX_PITCH || hipPitch >= MAX_PITCH)

    const params = {
      cache,
      ...formState,
      snowCatcherEquipmentKind,
      snowProtectionKind,
      snowGroundLoad,
      isSnowNose: isSnowNoseForPreCalc,
    }

    let cancelled = false
    api
      .postSnowLoadProtectionCalculation(params)
      .then((result) => {
        if (cancelled) return
        const primaryRows = result?.primary?.snowCatcherRows ?? null
        const secondaryRows = result?.secondary?.snowCatcherRows ?? null
        const excessive =
          (primaryRows !== null && primaryRows > MAX_SNOW_CATCHER_ROWS) ||
          (secondaryRows !== null && secondaryRows > MAX_SNOW_CATCHER_ROWS)
        setHasExcessiveRows(excessive)
      })
      .catch(() => {
        if (!cancelled) setHasExcessiveRows(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, snowCatcherEquipmentKind, snowProtectionKind, snowGroundLoad])

  /**
   * isSnowNose has to be true in austria if pitch is larger than 35
   */
  useEffect(() => {
    const locale = formState?.place.locale
    const roofPitch = formState?.dimensions?.roofPitch
    const hipPitch = formState?.dimensions?.hipPitch
    const isSnowNoseMandatory =
      locale === locales.austria && (roofPitch >= MAX_PITCH || hipPitch >= MAX_PITCH)
    setSnowNoseMandatory(isSnowNoseMandatory)
    if (isSnowNoseMandatory) {
      formik.setFieldValue('isSnowNose', true, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState?.dimensions, formState?.place])

  /**
   * Auto-enable snow nose when pre-calculation shows more than 2 rows
   */
  useEffect(() => {
    if (hasExcessiveRows) {
      formik.setFieldValue('isSnowNose', true, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasExcessiveRows])

  /**
   * If preloaded snowZone exists, set it automatically
   */
  useEffect(() => {
    formik.setFieldValue('snowZone', formState?.snowZone)
    formik.setFieldValue('snowGroundLoad', formState?.snowGroundLoad)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState?.snowZone])

  return (
    <div {...props}>
      <Section
        spacing="topLarge"
        headline={content.headline}
        subline={
          <>
            <span>{content.subline.text[0]}</span>{' '}
            <Link onClick={() => setSnowZoneModalOpen(true)}>{content.subline.link[0]}</Link>{' '}
            <span>{content.subline.text[1]}</span>{' '}
            <Link onClick={() => setSnowEquipmentModalOpen(true)}>{content.subline.link[1]}</Link>
            {', '}
            <Link onClick={() => setSnowProtectionModalOpen(true)}>
              {content.subline.link[2]}
            </Link>{' '}
            <span>{content.subline.text[2]}</span>
          </>
        }
      >
        <Grid container spacing={{ xs: 2, sm: 4 }}>
          <Grid item xs={12} sm={6}>
            <FormikTextField
              disabled
              formik={formik}
              label={protectionProps.snowZones.desc}
              name={protectionProps.snowZones.id}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormikTextField
              disabled
              formik={formik}
              label={protectionProps.snowGroundLoad.desc}
              name={protectionProps.snowGroundLoad.id}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {protectionProps.snowGroundLoad.unit}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormikSelect
              formik={formik}
              name={protectionProps.snowCatcherEquipmentKind.id}
              label={protectionProps.snowCatcherEquipmentKind.desc}
              options={equipmentOptions}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormikSelect
              formik={formik}
              name={protectionProps.snowProtectionKind.id}
              label={protectionProps.snowProtectionKind.desc}
              options={protectionProps.snowProtectionKind.items}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <FormikCheckbox
              disabled={isSnowNoseMandatory || hasExcessiveRows}
              formik={formik}
              name={protectionProps.isSnowNose.id}
              label={protectionProps.isSnowNose.desc}
            />
            {hasExcessiveRows && (
              <Alert severity="info" title={content.snowNoseRowsInfo} />
            )}
          </Grid>
        </Grid>
      </Section>

      {/* MODALS */}
      <SnowZoneModal
        locale={formState.place.locale}
        isOpen={isSnowZoneModalOpen}
        onClose={() => setSnowZoneModalOpen(false)}
      />
      <SnowEquipmentModal
        locale={formState.place.locale}
        bearingResistance={formState?.tile?.bearingResistance}
        isOpen={isSnowEquipmentModalOpen}
        onClose={() => setSnowEquipmentModalOpen(false)}
      />
      <SnowProtectionModal
        isOpen={isSnowProtectionModalOpen}
        onClose={() => setSnowProtectionModalOpen(false)}
      />
    </div>
  )
}
